import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectScenario } from '../scenarios/scenarioSlice';
import { selectFilteredEdgeNodes } from '../map/slice/mapSelectors';
import { selectPaths } from '../paths/pathsSlice';
import { generateColor } from '../utils/colors';
import { fromLonLat } from 'ol/proj';
import { RootState } from '@/store';
import { Node } from '@/types';
import { useMapLayer } from '../hooks/useMapLayer';
import { FeatureLike } from '../utils/geoJsonUtils';

const getOperationMarkerStyle = () => {
  return {
    'circle-radius': 18,
    'circle-fill-color': '#ffffff15',
    'circle-rotate-with-view': false,
    'circle-stroke-color': ['get', 'color'],
    'circle-stroke-width': 5,
  };
};

export type MapOperationMarkersProps = {
  zIndex: number;
};

export const MapOperationMarkers: React.FC<MapOperationMarkersProps> = ({
  zIndex,
}) => {
  const scenario = useSelector(selectScenario);
  const selectedPathId = useSelector(
    (state: RootState) => state.paths.selectedPathId,
  );
  const nodes = useSelector(selectFilteredEdgeNodes);
  const paths = useSelector(selectPaths);

  const path = useMemo(
    () => paths[selectedPathId ?? ''],
    [paths, selectedPathId],
  );

  const sortedOperations = useMemo(() => {
    if (!path) return [];
    const chainOperations = path?.operation_chain?.operations ?? [];
    return scenario.operations
      .filter((op) => {
        return chainOperations.includes(Number(op.id));
      })
      .sort((op1, op2) => {
        return (
          chainOperations.indexOf(Number(op1.id)) -
          chainOperations.indexOf(Number(op2.id))
        );
      });
  }, [path, scenario]);

  const sortedNodes = useMemo(() => {
    return sortedOperations.reduce<Node[]>((order, op) => {
      const receiptNode = nodes[op.receipt_point.node ?? ''];
      const deliveryNode = nodes[op.delivery_point.node ?? ''];
      if (receiptNode && order.length === 0) {
        order.push(receiptNode);
      } else if (receiptNode && order[order.length - 1] !== receiptNode) {
        order.push(receiptNode);
      }
      if (deliveryNode && order[order.length - 1] !== deliveryNode) {
        order.push(deliveryNode);
      }
      return order;
    }, []);
  }, [sortedOperations, nodes]);

  const operationFeatures = useMemo<FeatureLike[]>(
    () =>
      sortedNodes.map((node) => {
        return {
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: fromLonLat([Number(node.lng), Number(node.lat)]),
          },
          properties: {
            id: node.id,
            key: `node-${node.id}`,
            color: generateColor(selectedPathId ?? 0, 100, 50),
          },
        };
      }),
    [sortedNodes, selectedPathId],
  );

  const style = useMemo(() => getOperationMarkerStyle(), []);

  useMapLayer(operationFeatures, { zIndex, style });

  return <></>;
};
